import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {
  ForumOutlined as ForumOutlinedIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import colors from '../../assets/theme/colors';
import {
  canInviteAnyone,
  canManageTeam,
  getRoleDisplay,
} from '../../constants/roles';
import { canOpenDefaultChildChat } from '../../services/messaging/participantAccess';
import GradientButton from '../UI/GradientButton';
import {
  collectTeamMembers,
  getChildCareOwner,
  getReadableRoleLabel,
  getTeamRolePresentation,
} from './careTeamPresentation';
import { getCareTeamLensCopy } from './careTeamLensCopy';

const sectionCardSx = {
  mb: 2,
  borderRadius: 2.5,
  border: `1px solid ${colors.landing.borderLight}`,
  boxShadow: `0 6px 16px ${colors.landing.shadowSoft}`,
};

const mutedPanelSx = {
  borderRadius: 2,
  border: `1px solid ${alpha(colors.landing.borderMedium, 0.45)}`,
  bgcolor: colors.landing.sageLight,
};

const inviteActionButtonSx = {
  minHeight: 40,
  px: 1.75,
  borderRadius: 999,
  textTransform: 'none',
  fontSize: '0.84rem',
  fontWeight: 800,
  lineHeight: 1.15,
  color: colors.landing.heroText,
  whiteSpace: 'normal',
};

const isPendingInviteStatus = (status) => !['accepted', 'canceled', 'cancelled'].includes(
  String(status || 'pending').toLowerCase()
);

const InviteStatusRow = ({
  invite,
  getInviteStatusLabel,
  getInviteStatusTone,
  onResend,
  onEdit,
  onCancel,
  resendingInviteId,
  cancelingInviteId,
}) => {
  const tone = getInviteStatusTone(invite);
  const isErrorTone = tone === 'error';

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        border: `1px solid ${isErrorTone ? alpha(colors.careTeamView.inviteErrorText, 0.22) : colors.landing.borderLight}`,
        bgcolor: isErrorTone ? alpha(colors.careTeamView.inviteErrorText, 0.04) : colors.landing.surfaceSoft,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: colors.landing.heroText }}>
            {invite.recipientEmail}
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: colors.landing.textMuted, mt: 0.25 }}>
            {getReadableRoleLabel(invite.role)}{invite.multiChild ? ' • Multi-child' : ''}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={getInviteStatusLabel(invite)}
          color={tone}
          variant={tone === 'default' ? 'outlined' : 'filled'}
        />
      </Box>

      {invite.emailError ? (
        <Typography sx={{ fontSize: '0.78rem', color: colors.careTeamView.inviteErrorText, mt: 0.9 }}>
          {invite.emailError}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, auto)' },
          gap: 1,
          mt: 1.25,
          alignItems: 'stretch',
          justifyContent: { xs: 'stretch', sm: 'flex-start' },
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={() => onResend(invite)}
          disabled={resendingInviteId === invite.id}
          title="Send the same invitation email again"
          sx={inviteActionButtonSx}
        >
          {resendingInviteId === invite.id ? 'Sending...' : 'Resend email'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onEdit(invite)}
          title="Change the email, role, or note, then send a new invite email"
          sx={inviteActionButtonSx}
        >
          Edit and resend
        </Button>
        <Button
          size="small"
          color="error"
          variant="text"
          onClick={() => onCancel(invite)}
          disabled={cancelingInviteId === invite.id}
          title="Stop this pending invite link from being accepted"
          sx={{
            ...inviteActionButtonSx,
            gridColumn: { xs: '1 / -1', sm: 'auto' },
            color: colors.careTeamView.cancelActionText,
            justifySelf: { xs: 'start', sm: 'auto' },
          }}
        >
          {cancelingInviteId === invite.id ? 'Canceling...' : 'Cancel and revoke invite'}
        </Button>
      </Box>
    </Box>
  );
};

const CareTeamChildSection = ({
  child,
  viewerRole,
  currentUserId,
  invites,
  inviteLoading,
  onInviteChild,
  onOpenChat,
  getInviteStatusLabel,
  getInviteStatusTone,
  onResendInvite,
  onEditInvite,
  onCancelInvite,
  resendingInviteId,
  cancelingInviteId,
}) => {
  const allMembers = collectTeamMembers(child);
  const careOwner = getChildCareOwner(child);
  const activeInvites = invites.filter((invite) => isPendingInviteStatus(invite.status));
  const acceptedInvites = invites.filter((invite) => invite.status === 'accepted');
  const canManage = canManageTeam(viewerRole);
  const canInvite = canInviteAnyone(viewerRole);
  const canOpenChat = canOpenDefaultChildChat(viewerRole) && allMembers.length > 0;
  const isParticipantView = !canManage;
  const statsText = canManage
    ? `${allMembers.length} ${allMembers.length === 1 ? 'member' : 'members'} · ${activeInvites.length} pending ${activeInvites.length === 1 ? 'invite' : 'invites'}`
    : `${allMembers.length} ${allMembers.length === 1 ? 'member' : 'members'} on this team`;
  const currentMember = allMembers.find((member) => member.uid && member.uid === currentUserId) || null;
  const ownerName = careOwner?.name || careOwner?.displayName || 'Care Owner';
  const viewerRoleDisplay = getRoleDisplay(viewerRole);
  const participantRoleLabel = viewerRoleDisplay.plainLabel || getReadableRoleLabel(viewerRole);
  const careTeamCopy = getCareTeamLensCopy(child);
  const emptyTeamText = typeof careTeamCopy.emptyTeamText === 'function'
    ? careTeamCopy.emptyTeamText(child.name)
    : careTeamCopy.emptyTeamText;

  return (
    <Card variant="outlined" sx={sectionCardSx}>
      <Box sx={{ p: { xs: 1.75, md: 2 } }}>
        <Box
          sx={{
            mb: 1.5,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.22rem', fontWeight: 800, color: colors.brand.ink, lineHeight: 1.15 }}>
              {child.name}
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: '0.92rem', color: colors.landing.textMuted }}>
              {statsText}
            </Typography>
          </Box>
        </Box>

        {canInvite ? (
          <GradientButton
            variant="outlined"
            color="success"
            size="small"
            onClick={() => onInviteChild(child.id)}
            sx={{
              width: '100%',
              mb: 1.5,
              bgcolor: colors.landing.sageLight,
              borderColor: colors.landing.borderFocus,
              color: colors.semantic.sageDeep,
            }}
          >
            {careTeamCopy.inviteButtonLabel(child.name)}
          </GradientButton>
        ) : null}

        {isParticipantView ? (
          <Box
            sx={{
              mb: 1.5,
              p: 1.15,
              borderRadius: 2,
              border: `1px solid ${alpha(colors.landing.borderMedium, 0.38)}`,
              bgcolor: alpha(colors.landing.surfaceSoft, 0.88),
            }}
          >
            <Stack spacing={0.85}>
              <Typography sx={{ fontSize: '0.88rem', color: colors.landing.textMuted }}>
                <Box component="span" sx={{ fontWeight: 700, color: colors.landing.heroText }}>
                  Your role:
                </Box>{' '}
                {participantRoleLabel}
              </Typography>
              <Typography sx={{ fontSize: '0.88rem', color: colors.landing.textMuted }}>
                <Box component="span" sx={{ fontWeight: 700, color: colors.landing.heroText }}>
                  Care owner:
                </Box>{' '}
                {ownerName}
              </Typography>
              <Typography sx={{ fontSize: '0.84rem', color: colors.landing.textSoft, lineHeight: 1.45 }}>
                {careTeamCopy.participantRoleHint(viewerRoleDisplay.teamViewHint)}
              </Typography>
              {currentMember?.email ? (
                <Typography sx={{ fontSize: '0.83rem', color: colors.landing.textSoft }}>
                  Signed in as {currentMember.email}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}

        {canOpenChat ? (
          <Box sx={{ mb: 1.5 }}>
            <Button
              size="small"
              variant="text"
              startIcon={<ForumOutlinedIcon />}
              onClick={() => onOpenChat(child.id)}
              sx={{
                px: 0,
                minWidth: 0,
                textTransform: 'none',
                fontWeight: 700,
                color: colors.landing.textMuted,
              }}
            >
              Open chat
            </Button>
          </Box>
        ) : null}

        <Box sx={{ mb: canManage ? 0 : 0.5 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.landing.textMuted, mb: 1 }}>
            {canManage ? careTeamCopy.memberSectionTitle : careTeamCopy.participantMemberSectionTitle}
          </Typography>
        </Box>

        <Stack spacing={1}>
          {allMembers.length === 0 ? (
            <Box sx={{ ...mutedPanelSx, p: 1.25 }}>
              <Typography sx={{ fontSize: '0.96rem', color: colors.landing.textMuted }}>
                {emptyTeamText}
              </Typography>
            </Box>
          ) : (
            allMembers.map((member, index) => {
              const roleUi = getTeamRolePresentation(member.role, {signsOriginals:child.staffCaregivers?.[member.uid]?.enabled === true});

              return (
                <Box
                  key={`${member.uid || member.email || member.name}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.15,
                    borderRadius: 2,
                    border: `1px solid ${colors.landing.borderLight}`,
                    bgcolor: colors.landing.surfaceSoft,
                  }}
                >
                  <Avatar
                    src={member.photoURL}
                    sx={{
                      width: 42,
                      height: 42,
                      bgcolor: roleUi.secondary,
                      color: roleUi.accent,
                      fontWeight: 700,
                    }}
                  >
                    {member.name?.charAt(0) || member.displayName?.charAt(0) || '?'}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.96rem', fontWeight: 700, color: colors.landing.heroText }}>
                      {member.name || member.displayName || 'Unknown User'}
                      {member.uid && member.uid === currentUserId ? (
                        <Box component="span" sx={{ ml: 0.65, fontSize: '0.8rem', fontWeight: 600, color: colors.landing.textMuted }}>
                          (You)
                        </Box>
                      ) : null}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: colors.landing.textMuted, mt: 0.15 }}>
                      {member.email || 'No email available'}
                    </Typography>
                    {careTeamCopy.roleHelp?.[member.role] ? (
                      <Typography sx={{ fontSize: '0.8rem', color: colors.landing.textSoft, mt: 0.35, lineHeight: 1.35 }}>
                        {careTeamCopy.roleHelp[member.role]}
                      </Typography>
                    ) : null}
                  </Box>

                  <Chip
                    label={roleUi.label}
                    size="small"
                    sx={{
                      bgcolor: roleUi.chipBg,
                      color: roleUi.accent,
                      border: `1px solid ${roleUi.chipBorder}`,
                      fontWeight: 700,
                    }}
                  />
                </Box>
              );
            })
          )}
        </Stack>

        {canManage ? (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.landing.textMuted, mb: 1 }}>
              {careTeamCopy.pendingInvitesTitle}
            </Typography>

            {inviteLoading ? (
              <Box sx={{ ...mutedPanelSx, p: 1.25 }}>
                <Typography sx={{ fontSize: '0.92rem', color: colors.landing.textMuted }}>
                  Loading invite status...
                </Typography>
              </Box>
            ) : activeInvites.length === 0 ? (
              <Box sx={{ ...mutedPanelSx, p: 1.25 }}>
                <Typography sx={{ fontSize: '0.96rem', color: colors.landing.textMuted }}>
                  {careTeamCopy.noPendingInvitesText}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {activeInvites.map((invite) => (
                  <InviteStatusRow
                    key={invite.id}
                    invite={invite}
                    getInviteStatusLabel={getInviteStatusLabel}
                    getInviteStatusTone={getInviteStatusTone}
                    onResend={onResendInvite}
                    onEdit={onEditInvite}
                    onCancel={onCancelInvite}
                    resendingInviteId={resendingInviteId}
                    cancelingInviteId={cancelingInviteId}
                  />
                ))}
              </Stack>
            )}

            {acceptedInvites.length > 0 ? (
              <Typography sx={{ mt: 1, fontSize: '0.85rem', color: colors.landing.textMuted }}>
                {careTeamCopy.acceptedInvitesText(acceptedInvites.length)}
              </Typography>
            ) : null}
          </>
        ) : acceptedInvites.length > 0 ? (
          <Typography sx={{ mt: 1.1, fontSize: '0.85rem', color: colors.landing.textMuted }}>
            {careTeamCopy.acceptedInvitesText(acceptedInvites.length)}
          </Typography>
        ) : null}
      </Box>
    </Card>
  );
};

export default CareTeamChildSection;
